<template>
  <section id="dashboard-ecommerce">
    <!-- Tabs -->
    <div class="dashboard-tabs-container">
      <b-tabs
        v-model="activeTab"
        class="dashboard-tabs"
        :style="{
          '--theme-color': themeColors.theme_color
        }"
        @input="onTabChange"
      >
        <!-- Finance Tab -->
        <b-tab title="Finance">
          <div class="dashboard-widgets-container">
            <!-- Financial Statistics Section -->
            <div class="widget-section">
              <skeleton-loader
                v-if="loading.financialStats"
                type="financial-stats"
              />
              <dashboard-financial-statistics
                v-else
                :data="tempdata.financialstatisticsItems"
                :last-updated-at="financialStatsTimestamps.last_generated_at"
                :next-generated-at="financialStatsTimestamps.next_generated_at"
                :theme-colors="themeColors"
                :is-refreshing="isRefreshing"
                :on-refresh="handleInvoiceSummaryRefresh"
              />
            </div>

            <!-- Monthly Earnings & To Be Invoices Widgets -->
            <div class="widget-section">
              <b-row class="match-height widgets-row">
                <b-col
                  md="6"
                  class="widget-col"
                >
                  <skeleton-loader
                    v-if="loading.codIncome"
                    type="chart"
                  />
                  <dashboard-monthly-earnings
                    v-else
                    :data="tempdata.monthlyEarnings"
                    :last-updated-at="financialStatsTimestamps.last_generated_at"
                    :on-refresh="handleMonthlyEarningsRefresh"
                    :is-refreshing="loading.codIncome"
                  />
                </b-col>
                <b-col
                  md="6"
                  class="widget-col"
                >
                  <lazy-load-observer :on-visible="loadToBeInvoices">
                    <template #placeholder>
                      <skeleton-loader type="transactions" />
                    </template>
                    <template #content>
                      <skeleton-loader
                        v-if="loading.toBeInvoices"
                        type="transactions"
                      />
                      <dashboard-to-be-invoices
                        v-else
                        :data="tempdata.toBeInvoices"
                        :last-updated-at="toBeInvoicesLastUpdated"
                        :on-refresh="handleToBeInvoicesRefresh"
                        :is-refreshing="loading.toBeInvoices"
                      />
                    </template>
                  </lazy-load-observer>
                </b-col>
              </b-row>
            </div>

            <!-- Monthly Delivery Charge Widget -->
            <div class="widget-section">
              <lazy-load-observer :on-visible="loadMonthlyDeliveryCharge">
                <template #placeholder>
                  <skeleton-loader type="chart" />
                </template>
                <template #content>
                  <skeleton-loader
                    v-if="loading.monthlyDeliveryCharge"
                    type="chart"
                  />
                  <dashboard-monthly-delivery-charge
                    v-else
                    title="Monthly Delivery Charge"
                    type="line"
                    :data="tempdata.monthlyDeliveryCharge"
                    :last-updated-at="financialStatsTimestamps.last_generated_at"
                    :theme-colors="themeColors"
                    :on-refresh="handleMonthlyDeliveryChargeRefresh"
                    :is-refreshing="loading.monthlyDeliveryCharge"
                  />
                </template>
              </lazy-load-observer>
            </div>
          </div>
        </b-tab>

        <!-- Orders Tab -->
        <b-tab title="Orders">
          <div class="dashboard-widgets-container">
            <!-- Order Statistics -->
            <div class="widget-section">
              <skeleton-loader
                v-if="loading.orderStats"
                type="statistics"
              />
              <dashboard-order-statistics
                v-else
                :data="tempdata.statisticsItems"
                :last-updated-at="statusStatsLastUpdated"
                :on-refresh="handleOrderStatsRefresh"
                :is-refreshing="loading.orderStats"
              />
            </div>

            <!-- Monthly Orders -->
            <div class="widget-section">
              <skeleton-loader
                v-if="loading.monthlyOrders"
                type="chart"
              />
              <dashboard-monthly-orders
                v-else
                :data="tempdata.monthlyOrders"
                :last-updated-at="statusStatsLastUpdated"
                :on-refresh="handleMonthlyOrdersRefresh"
                :is-refreshing="refreshing.monthlyOrders"
              />
            </div>

            <!-- Status Statistics - Lazy Load on Scroll -->
            <div class="widget-section">
              <lazy-load-observer :on-visible="loadStatusStatistics">
                <template #placeholder>
                  <skeleton-loader
                    type="table"
                    :rows="8"
                  />
                </template>
                <template #content>
                  <skeleton-loader
                    v-if="loading.statusStats"
                    type="table"
                    :rows="8"
                  />
                  <dashboard-status-statistics
                    v-else
                    :table-data="tempdata.companyTable.data"
                    :last-updated-at="statusStatsLastUpdated"
                    :on-refresh="handleStatusStatsRefresh"
                    :is-refreshing="loading.statusStats"
                  />
                </template>
              </lazy-load-observer>
            </div>
          </div>
        </b-tab>

        <!-- Locations Tab -->
        <b-tab title="Locations">
          <div class="dashboard-widgets-container">
            <!-- Citywise Delivery Statistics - Lazy Load on Scroll -->
            <div class="widget-section">
              <lazy-load-observer :on-visible="loadCitywiseStatistics">
                <template #placeholder>
                  <skeleton-loader type="chart" />
                </template>
                <template #content>
                  <skeleton-loader
                    v-if="loading.citywiseStats"
                    type="chart"
                  />
                  <dashboard-citywise-delivery-statistics
                    v-else
                    :data="tempdata.cityWiseStats"
                    :last-updated-at="statusStatsLastUpdated"
                    :on-refresh="handleCitywiseStatsRefresh"
                    :is-refreshing="loading.citywiseStats"
                  />
                </template>
              </lazy-load-observer>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </section>
</template>

<script>
import {
  BRow, BCol, BTabs, BTab,
} from 'bootstrap-vue'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import SkeletonLoader from '@/components/Dashboard/SkeletonLoader.vue'
import LazyLoadObserver from '@/components/Dashboard/LazyLoadObserver.vue'
import DashboardFinancialStatistics from '@/components/Dashboard/DashboardFinancialStatistics.vue'
import DashboardMonthlyEarnings from '@/components/Dashboard/DashboardMonthlyEarnings.vue'
import DashboardToBeInvoices from '@/components/Dashboard/DashboardToBeInvoices.vue'
import DashboardMonthlyDeliveryCharge from '@/components/Dashboard/DashboardMonthlyDeliveryCharge.vue'
import DashboardOrderStatistics from '@/components/Dashboard/DashboardOrderStatistics.vue'
import DashboardMonthlyOrders from '@/components/Dashboard/DashboardMonthlyOrders.vue'
import DashboardStatusStatistics from '@/components/Dashboard/DashboardStatusStatistics.vue'
import DashboardCitywiseDeliveryStatistics from '@/components/Dashboard/DashboardCitywiseDeliveryStatistics.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const DashboardRepository = RepositoryFactory.get('dashboard')
const BusinessSettingsRepository = RepositoryFactory.get('businessSettings')

export default {
  components: {
    BRow,
    BCol,
    BTabs,
    BTab,
    SkeletonLoader,
    LazyLoadObserver,
    DashboardFinancialStatistics,
    DashboardMonthlyEarnings,
    DashboardToBeInvoices,
    DashboardMonthlyDeliveryCharge,
    DashboardOrderStatistics,
    DashboardMonthlyOrders,
    DashboardStatusStatistics,
    DashboardCitywiseDeliveryStatistics,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      activeTab: 0,
      tabsLoaded: {
        orders: false,
        finance: false,
        locations: false,
      },
      loading: {
        orderStats: false,
        monthlyOrders: false,
        statusStats: false,
        financialStats: false,
        codIncome: false,
        toBeInvoices: false,
        monthlyDeliveryCharge: false,
        citywiseStats: false,
      },
      refreshing: {
        monthlyOrders: false,
      },
      businessProfileCurrency: null,
      data: {},
      lables: [],
      statusStatsLastUpdated: null,
      toBeInvoicesLastUpdated: null,
      financialStatsTimestamps: {
        last_generated_at: null,
        next_generated_at: null,
      },
      isRefreshing: false,
      nextUpdateInterval: null,
      themeColors: {
        theme_color: '#7367f0',
        gradientColor1: '#7367f0',
        gradientColor2: '#00cfe8',
        gradientDirection: 'to right',
      },
      tempdata: {
        statisticsItems: [
          {
            icon: 'BoxIcon',
            customClass: '',
            color: 'light-info',
            title: 0,
            subtitle: 'Orders Count',
          },
          {
            icon: 'DollarSignIcon',
            customClass: 'ml-3',
            color: 'light-success',
            title: 0,
            subtitle: 'Delivery Charge',
          },
        ],
        financialstatisticsItems: [
          {
            icon: 'ShoppingBagIcon',
            customClass: '',
            color: 'light-info',
            title: 0,
            subtitle: 'Invoice Value',
          },
          {
            icon: 'LoaderIcon',
            customClass: '',
            color: 'light-warning',
            title: 0,
            subtitle: 'Pending Invoice',
          },
          {
            icon: 'CheckCircleIcon',
            customClass: '',
            color: 'light-success',
            title: 0,
            subtitle: 'Paid Invoice Value',
          },
          {
            icon: 'UserCheckIcon',
            customClass: '',
            color: 'light-primary',
            title: 0,
            subtitle: 'Approved Invoice',
          },
        ],
        monthlyEarnings: {
          categories: { lables: [] },
          series: [
            {
              name: 'COD',
              data: [],
            },
          ],
        },
        codReceivable: {
          series: [],
        },
        toBeInvoices: [
          {
            avatarVariant: 'light-warning',
            avatar: 'CodesandboxIcon',
            mode: 'Collected COD',
            types: this.businessProfileCurrency || (localStorage.getItem('businessCurrency') === 'ALL' ? 'LKR' : localStorage.getItem('businessCurrency')),
            deduction: false,
            payment: 0,
          },
          {
            avatarVariant: 'light-success',
            avatar: 'TruckIcon',
            mode: 'Delivery Charges',
            types: this.businessProfileCurrency || (localStorage.getItem('businessCurrency') === 'ALL' ? 'LKR' : localStorage.getItem('businessCurrency')),
            deduction: false,
            payment: 0,
          },
          {
            avatarVariant: 'light-info',
            avatar: 'PercentIcon',
            mode: 'Commission',
            types: this.businessProfileCurrency || (localStorage.getItem('businessCurrency') === 'ALL' ? 'LKR' : localStorage.getItem('businessCurrency')),
            deduction: false,
            payment: 0,
          },
          {
            avatarVariant: 'light-primary',
            avatar: 'DollarSignIcon',
            mode: 'Invoice Value',
            types: this.businessProfileCurrency || (localStorage.getItem('businessCurrency') === 'ALL' ? 'LKR' : localStorage.getItem('businessCurrency')),
            deduction: false,
            payment: 0,
          },
        ],
        monthlyOrders: {
          categories: { lables: [] },
          series: [
            { data: [] },
          ],
        },
        monthlyDeliveryCharge: {
          categories: { lables: [] },
          series: [
            {
              name: 'Delivery',
              data: [],
            },
            {
              name: 'Partially',
              data: [],
            },
            {
              name: 'Return',
              data: [],
            },
          ],
        },
        cityWiseStats: {
          categories: { lables: [] },
          series: [
            {
              name: 'Delivered orders',
              data: [],
            },
          ],
        },
        companyTable: {
          data: [],
        },
      },
    }
  },
  computed: {},
  async mounted() {
    // Get theme colors from CSS variables (navigation bar gradient)
    this.getThemeColors()
    // Initialize all widget refresh times in localStorage
    this.initializeWidgetRefreshTimes()
    // Load default tab (Finance) data
    this.loadFinanceTab()
    // Start interval to update next update time
    this.startNextUpdateInterval()
  },
  beforeDestroy() {
    if (this.nextUpdateInterval) {
      clearInterval(this.nextUpdateInterval)
    }
  },
  created() {
    this.getBusinessCurrency()
  },
  methods: {
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    startNextUpdateInterval() {
      // Update every minute
      this.nextUpdateInterval = setInterval(() => {
        this.$forceUpdate()
      }, 60000)
    },
    onTabChange(tabIndex) {
      // Tab 0: Finance, Tab 1: Orders, Tab 2: Locations
      if (tabIndex === 0 && !this.tabsLoaded.finance) {
        this.loadFinanceTab()
      } else if (tabIndex === 1 && !this.tabsLoaded.orders) {
        this.loadOrdersTab()
      } else if (tabIndex === 2 && !this.tabsLoaded.locations) {
        this.loadLocationsTab()
      }
    },
    async loadOrdersTab() {
      if (this.tabsLoaded.orders) return

      // Load visible widgets immediately
      this.readDataforOrderStats()
      this.readDataforMonthlyOrders()
      // Note: Status Statistics loads when user scrolls to it (lazy loaded)

      // Mark tab as loaded to prevent re-loading
      this.tabsLoaded.orders = true
    },
    async loadFinanceTab() {
      if (this.tabsLoaded.finance) return

      // Load visible widgets immediately
      this.readDataforFinancialStats()
      this.readDataforMonthlyEarnings()
      // Note: To Be Invoices and Monthly Delivery Charge load when user scrolls to them

      // Mark tab as loaded to prevent re-loading
      this.tabsLoaded.finance = true
    },
    async loadLocationsTab() {
      if (this.tabsLoaded.locations) return

      // Note: Citywise Statistics loads when user scrolls to it (lazy loaded)

      // Mark tab as loaded to prevent re-loading
      this.tabsLoaded.locations = true
    },
    loadStatusStatistics() {
      // Called when Status Statistics widget enters viewport
      this.readDataforStatusStatistics()
    },
    loadCitywiseStatistics() {
      // Called when Citywise Statistics widget enters viewport
      this.readDataforCitywiseStats()
    },
    loadToBeInvoices() {
      // Called when To Be Invoices widget enters viewport
      this.readDataforToBeInvoices()
    },
    loadMonthlyDeliveryCharge() {
      // Called when Monthly Delivery Charge widget enters viewport
      this.readDataforMonthlyDeliveryCharge()
    },
    async getBusinessCurrency() {
      try {
        // eslint-disable-next-line no-unused-vars
        const { data: { company_currency } } = (await BusinessSettingsRepository.getBusinessSettigs()).data
        // eslint-disable-next-line camelcase
        this.businessProfileCurrency = company_currency
        localStorage.setItem('businessCurrency', company_currency)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    getThemeColors() {
      // Get colors from CSS variables (same as navigation bar)
      const root = document.documentElement
      const computedStyle = getComputedStyle(root)

      this.themeColors = {
        theme_color: computedStyle.getPropertyValue('--primary').trim() || '#7367f0',
        gradientColor1: computedStyle.getPropertyValue('--gradientColor1').trim() || '#7367f0',
        gradientColor2: computedStyle.getPropertyValue('--gradientColor2').trim() || '#00cfe8',
        gradientDirection: computedStyle.getPropertyValue('--gradientDirection').trim() || 'to right',
      }
    },
    async readDataforOrderStats() {
      this.loading.orderStats = true
      try {
        const response = await DashboardRepository.getOrderStatsFinance()
        const { data } = response.data
        this.tempdata.statisticsItems[0].title = data.order_count
        this.tempdata.statisticsItems[1].title = data.total_delivery_charge
        // Extract last_updated_at from data object (it's inside data, not at root level)
        const lastUpdatedAt = data.last_updated_at
        // Share the same last_updated_at with Status Statistics (same Redis value)
        // Always update the timestamp to keep both components in sync
        this.statusStatsLastUpdated = lastUpdatedAt || new Date().toISOString()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading.orderStats = false
    },
    async handleOrderStatsRefresh() {
      // Refresh only Order Statistics
      await this.readDataforOrderStats()
      // Save refresh time to localStorage
      const now = new Date().toISOString()
      const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
      refreshTimes.orderstatistics = now
      localStorage.setItem('dashboardwidgetsrefreshtimes', JSON.stringify(refreshTimes))
    },
    async readDataforFinancialStats() {
      this.loading.financialStats = true
      try {
        const response = await DashboardRepository.getFinancialStatFinance()
        const { data } = response.data
        this.tempdata.financialstatisticsItems[0].title = data.total_invoice_amount
        this.tempdata.financialstatisticsItems[1].title = data.status_amount.filter(item => item.status_key === 'f_key_1').length > 0 ? data.status_amount.filter(item => item.status_key === 'f_key_1')[0].amount : 0
        this.tempdata.financialstatisticsItems[2].title = data.status_amount.filter(item => item.status_key === 'f_key_2').length > 0 ? data.status_amount.filter(item => item.status_key === 'f_key_2')[0].amount : 0
        this.tempdata.financialstatisticsItems[3].title = data.status_amount.filter(item => item.status_key === 'f_key_3').length > 0 ? data.status_amount.filter(item => item.status_key === 'f_key_3')[0].amount : 0
        // Store timestamp information - use $set to ensure reactivity
        this.$set(this.financialStatsTimestamps, 'last_generated_at', data.last_generated_at || null)
        this.$set(this.financialStatsTimestamps, 'next_generated_at', data.next_generated_at || null)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading.financialStats = false
    },
    async readDataforMonthlyOrders(skipLoading = false) {
      if (!skipLoading) {
        this.loading.monthlyOrders = true
      }
      try {
        const response = await DashboardRepository.getMonthlyOrdersFinance()
        const { data } = response.data
        this.tempdata.monthlyOrders.categories.lables = data.map(n => (`${n.month} ${n.year}`)).reverse().slice(-12)
        this.tempdata.monthlyOrders.series[0].data = data.map(n => n.data).reverse().slice(-12)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      if (!skipLoading) {
        this.loading.monthlyOrders = false
      }
    },
    async handleMonthlyOrdersRefresh() {
      if (this.refreshing.monthlyOrders) return // Prevent multiple simultaneous refreshes
      this.refreshing.monthlyOrders = true
      try {
        // Refresh data directly without calling refreshMonthlyOrdersSummary()
        // The monthly orders summary will update automatically in 5 seconds
        await this.readDataforMonthlyOrders(true) // Skip loading state to keep chart visible
        // Save refresh time to localStorage
        const now = new Date().toISOString()
        const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
        refreshTimes.monthlyorders = now
        localStorage.setItem('dashboardwidgetsrefreshtimes', JSON.stringify(refreshTimes))
        this.refreshing.monthlyOrders = false
      } catch (e) {
        this.convertAndNotifyError(e)
        this.refreshing.monthlyOrders = false
      }
    },
    async readDataforMonthlyEarnings() {
      this.loading.codIncome = true
      try {
        const { data } = (await DashboardRepository.getMonthlyEarningsFinance()).data
        this.tempdata.monthlyEarnings.categories.lables = data.map(n => (`${n.month} ${n.year}`)).reverse().slice(-12)
        this.tempdata.monthlyEarnings.series[0].data = data.map(n => n.monthly_cod_income).reverse().slice(-12)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading.codIncome = false
    },
    async readDataforToBeInvoices() {
      this.loading.toBeInvoices = true
      try {
        const { data } = (await DashboardRepository.getToBEInvoicesFinance()).data
        if (data[0].collected_cod === null) {
          this.tempdata.toBeInvoices[0].payment = 0
        } else {
          this.tempdata.toBeInvoices[0].payment = data[0].collected_cod
        }
        if (data[0].delivery_charge === null) {
          this.tempdata.toBeInvoices[1].payment = 0
        } else {
          this.tempdata.toBeInvoices[1].payment = data[0].delivery_charge
        }
        if (data[0].commision === null) {
          this.tempdata.toBeInvoices[2].payment = 0
        } else {
          this.tempdata.toBeInvoices[2].payment = data[0].commision
        }
        if (data[0].to_be_invoice_amount === null) {
          this.tempdata.toBeInvoices[3].payment = 0
        } else {
          this.tempdata.toBeInvoices[3].payment = data[0].to_be_invoice_amount
        }
        // Set last updated timestamp
        this.toBeInvoicesLastUpdated = new Date().toISOString()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading.toBeInvoices = false
    },
    async readDataforMonthlyDeliveryCharge() {
      this.loading.monthlyDeliveryCharge = true
      try {
        const { data } = (await DashboardRepository.getmonthlyDeliveryChargeFinance()).data
        this.lables = Object.keys(data)
        // Sort by date and get last 12 months
        const sortedKeys = this.lables.sort((a, b) => {
          const dateA = new Date(a.replace(/(\d{4}) (\w+)/, '$2 1, $1'))
          const dateB = new Date(b.replace(/(\d{4}) (\w+)/, '$2 1, $1'))
          return dateA - dateB
        }).slice(-12)
        this.tempdata.monthlyDeliveryCharge.categories.lables = sortedKeys
        if (data) {
          this.tempdata.monthlyDeliveryCharge.series[0].data = sortedKeys.map(key => data[key].delivery)
          this.tempdata.monthlyDeliveryCharge.series[1].data = sortedKeys.map(key => data[key].partially)
          this.tempdata.monthlyDeliveryCharge.series[2].data = sortedKeys.map(key => data[key].return)
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading.monthlyDeliveryCharge = false
    },
    async readDataforStatusStatistics() {
      this.loading.statusStats = true
      try {
        const response = await DashboardRepository.getStatusStatisticsFinance()
        const { data, last_updated_at: lastUpdatedAt } = response.data
        this.tempdata.companyTable.data = data
        // Use the last_updated_at from API response
        this.statusStatsLastUpdated = lastUpdatedAt || new Date().toISOString()
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading.statusStats = false
    },
    async handleStatusStatsRefresh() {
      // Refresh only Status Statistics
      await this.readDataforStatusStatistics()
      // Save refresh time to localStorage
      const now = new Date().toISOString()
      const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
      refreshTimes.statusstatistics = now
      localStorage.setItem('dashboardwidgetsrefreshtimes', JSON.stringify(refreshTimes))
    },
    async readDataforCitywiseStats() {
      this.loading.citywiseStats = true
      try {
        const response = await DashboardRepository.getCityWiseStats()
        const { data } = response.data
        // Store city names only in categories
        this.tempdata.cityWiseStats.categories.lables = data.map(n => n.city_name)
        // Store warehouse names separately
        this.tempdata.cityWiseStats.warehouseNames = data.map(n => n.warehouse_name)
        this.tempdata.cityWiseStats.series[0].data = data.map(n => n.count)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.loading.citywiseStats = false
    },
    async handleCitywiseStatsRefresh() {
      // Refresh only Citywise Delivery Statistics
      await this.readDataforCitywiseStats()
      // Save refresh time to localStorage
      const now = new Date().toISOString()
      const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
      refreshTimes.citywisedeliverystatistics = now
      localStorage.setItem('dashboardwidgetsrefreshtimes', JSON.stringify(refreshTimes))
    },
    async handleInvoiceSummaryRefresh() {
      this.isRefreshing = true
      try {
        await DashboardRepository.refreshInvoiceSummary()
        this.showSuccessMessage('Invoice summary refresh initiated. Data will update in 10 seconds.')
        // Wait 10 seconds for backend to process the refresh, then update data
        setTimeout(async () => {
          await this.readDataforFinancialStats()
          await this.readDataforMonthlyEarnings()
          await this.readDataforToBeInvoices()
          await this.readDataforMonthlyDeliveryCharge()
          this.isRefreshing = false
        }, 10000)
      } catch (e) {
        this.convertAndNotifyError(e)
        this.isRefreshing = false
      }
    },
    initializeWidgetRefreshTimes() {
      // Save current time for all widgets when dashboard is entered
      const now = new Date().toISOString()
      const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')

      // Initialize all widget keys with current time if they don't exist
      const widgetKeys = [
        'orderstatistics',
        'monthlyorders',
        'statusstatistics',
        'citywisedeliverystatistics',
        'monthlyearnings',
        'tobeinvoices',
        'monthlydeliverycharge',
      ]

      widgetKeys.forEach(key => {
        if (!refreshTimes[key]) {
          refreshTimes[key] = now
        }
      })

      localStorage.setItem('dashboardwidgetsrefreshtimes', JSON.stringify(refreshTimes))
    },
    async handleMonthlyEarningsRefresh() {
      // Refresh only Monthly Earnings
      await this.readDataforMonthlyEarnings()
      // Save refresh time to localStorage
      const now = new Date().toISOString()
      const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
      refreshTimes.monthlyearnings = now
      localStorage.setItem('dashboardwidgetsrefreshtimes', JSON.stringify(refreshTimes))
    },
    async handleToBeInvoicesRefresh() {
      // Refresh only To Be Invoices
      await this.readDataforToBeInvoices()
      // Save refresh time to localStorage
      const now = new Date().toISOString()
      const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
      refreshTimes.tobeinvoices = now
      localStorage.setItem('dashboardwidgetsrefreshtimes', JSON.stringify(refreshTimes))
    },
    async handleMonthlyDeliveryChargeRefresh() {
      // Refresh only Monthly Delivery Charge
      await this.readDataforMonthlyDeliveryCharge()
      // Save refresh time to localStorage
      const now = new Date().toISOString()
      const refreshTimes = JSON.parse(localStorage.getItem('dashboardwidgetsrefreshtimes') || '{}')
      refreshTimes.monthlydeliverycharge = now
      localStorage.setItem('dashboardwidgetsrefreshtimes', JSON.stringify(refreshTimes))
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../@core/scss/vue/pages/dashboard-ecommerce.scss';
@import '../@core/scss/vue/libs/chart-apex.scss';

.dashboard-tabs-container {
  background: #f8f8f8;
  border-radius: 0.357rem;
  padding: 0.5rem 1rem;
  margin-bottom: 0.75rem;
}

.dashboard-tabs ::v-deep .nav-tabs {
  border-bottom: none;
  margin-bottom: 0.5rem;
}

.dashboard-tabs ::v-deep .nav-item {
  margin-right: 2rem;
}

.dashboard-tabs ::v-deep .nav-link {
  border: none;
  background: transparent;
  color: #6e6b7b;
  font-size: 1rem;
  font-weight: 500;
  padding: 0.75rem 0;
  position: relative;
  transition: all 0.3s ease;
}

.dashboard-tabs ::v-deep .nav-link:hover {
  color: var(--theme-color, #7367f0);
  background: transparent;
  border: none;
}

.dashboard-tabs ::v-deep .nav-link.active {
  color: var(--theme-color, #7367f0);
  background: transparent;
  border: none;
  font-weight: 600;
}

.dashboard-tabs ::v-deep .nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: var(--theme-color, #7367f0);
  border-radius: 2px 2px 0 0;
}

.dashboard-tabs ::v-deep .tab-content {
  padding: 0;
  margin: 0 -1rem;
  width: calc(100% + 2rem);
}

.dashboard-widgets-container {
  display: flex;
  flex-direction: column;
  gap: 0rem;
}

.widget-section {
  width: 100%;
  margin-bottom: -1rem;
}

.widget-section:last-child {
  margin-bottom: 0;
}

.widgets-row {
  display: flex;
  align-items: stretch;
  margin: 0 -0.50rem;
}

.widgets-row > .widget-col {
  padding: 0 0.50rem;
}

.widget-col {
  display: flex;
  flex-direction: column;
}

.widget-col > * {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.section-title {
  .chart-title {
    font-size: 1.285rem;
    font-weight: 600;
    color: #5e5873;
  }
}

@media (max-width: 768px) {
  .update-banner-body {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .update-banner-text {
    margin-right: 0;
    margin-bottom: 0.75rem;
    width: 100%;
  }

  .dashboard-widgets-container {
    gap: 1rem;
  }
}
</style>
